import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { SignatureResponse } from '../services/signature';
import { useValidatedTenantId } from './auth/use-validated-tenant-id';
import { signatureQueryKeys } from './signature/query-keys';
import {
  useConfirmSignature,
  useDeleteSignature,
  useSaveSignature,
} from './signature/use-signature-mutations';
import { useMySignature } from './signature/use-signature-queries';

interface UseUserSignatureReturn {
  /** URL della firma esistente (presigned) */
  signatureUrl: string | null;
  /** Indica se i dati sono in caricamento */
  isLoading: boolean;
  /** Indica se il salvataggio è in corso */
  isSaving: boolean;
  /** Eventuale errore */
  error: string | null;
  /** Indica se l'utente ha una firma salvata */
  hasSignature: boolean;
  /**
   * True quando la firma corrente e' stata replicata da un'altra sede e non
   * e' ancora stata riaffermata su questa (Fase 1 "firma per sede").
   */
  needsConfirmation: boolean;
  /** Indica se la conferma della firma replicata e' in corso */
  isConfirming: boolean;
  /** Salva una nuova firma */
  saveSignature: (dataUrl: string) => Promise<SignatureResponse>;
  /** Conferma la firma replicata sulla sede corrente (idempotente) */
  confirmSignature: () => Promise<SignatureResponse>;
  /** Elimina la firma esistente */
  deleteSignature: () => Promise<void>;
  /** Ricarica la firma */
  reload: () => Promise<void>;
  /** Pulisce l'errore */
  clearError: () => void;
}

/**
 * Hook per gestire la firma dell'utente corrente.
 * Carica automaticamente la firma al primo utilizzo.
 *
 * Internamente usa TanStack Query per gestione cache e stato.
 */
function useUserSignature(): UseUserSignatureReturn {
  const { t } = useTranslation('signature');
  const queryClient = useQueryClient();
  const currentTenantId = useValidatedTenantId();

  const query = useMySignature();
  const saveMutation = useSaveSignature();
  const deleteMutation = useDeleteSignature();
  const confirmMutation = useConfirmSignature();

  const signatureUrl = query.data?.signatureUrl ?? null;
  const hasSignature = query.data?.hasSignature ?? false;
  const needsConfirmation = query.data?.needsConfirmation ?? false;

  const loadError = query.isError ? t('errors.loadError') : null;
  const saveError = saveMutation.isError ? t('errors.saveError') : null;
  const deleteError = deleteMutation.isError ? t('errors.deleteError') : null;
  const error = loadError ?? saveError ?? deleteError;

  // NOTE: depend on the stable `mutateAsync`/`reset` methods (TanStack Query
  // guarantees these are referentially stable) rather than the whole mutation
  // object, whose reference changes on every status transition (idle -> pending
  // -> success). Using the whole object made `saveSignature` identity change
  // during a save, which cascaded into consumers re-memoising their handlers
  // mid-flight and, in some render orderings, re-triggered the save.
  const saveMutateAsync = saveMutation.mutateAsync;
  const deleteMutateAsync = deleteMutation.mutateAsync;
  const confirmMutateAsync = confirmMutation.mutateAsync;
  const saveReset = saveMutation.reset;
  const deleteReset = deleteMutation.reset;

  const saveSignature = useCallback(
    async (dataUrl: string): Promise<SignatureResponse> => {
      return saveMutateAsync(dataUrl);
    },
    [saveMutateAsync],
  );

  const confirmSignature = useCallback(async (): Promise<SignatureResponse> => {
    return confirmMutateAsync();
  }, [confirmMutateAsync]);

  const deleteSignature = useCallback(async (): Promise<void> => {
    await deleteMutateAsync();
  }, [deleteMutateAsync]);

  const reload = useCallback(async (): Promise<void> => {
    if (!currentTenantId) {
      return;
    }
    await queryClient.invalidateQueries({ queryKey: signatureQueryKeys.me(currentTenantId) });
  }, [queryClient, currentTenantId]);

  const clearError = useCallback(() => {
    saveReset();
    deleteReset();
  }, [saveReset, deleteReset]);

  return {
    signatureUrl,
    isLoading: query.isLoading,
    isSaving: saveMutation.isPending || deleteMutation.isPending,
    error,
    hasSignature,
    needsConfirmation,
    isConfirming: confirmMutation.isPending,
    saveSignature,
    confirmSignature,
    deleteSignature,
    reload,
    clearError,
  };
}

export { useUserSignature };
